import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Flag, ShieldAlert, ExternalLink, Check, X } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const STATUS_LABEL = { open: "Offen", in_review: "In Prüfung", actioned: "Entfernt/Gesperrt", rejected: "Abgelehnt" };
const STATUS_STYLE = {
    open: "bg-amber-100 text-amber-800", in_review: "bg-blue-100 text-blue-800",
    actioned: "bg-red-100 text-red-700", rejected: "bg-gray-100 text-gray-600",
};

export default function AdminListingReportsPage() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [filter, setFilter] = useState("open");
    const [busy, setBusy] = useState(false);
    const [note, setNote] = useState({});

    const load = useCallback(async () => {
        setBusy(true);
        try {
            const q = filter === "all" ? "" : `?status=${filter}`;
            const { data } = await api.get(`/admin/listing-reports${q}`);
            setData(data);
        } catch { toast.error("Meldungen konnten nicht geladen werden"); }
        finally { setBusy(false); }
    }, [filter]);

    useEffect(() => {
        if (loading) return;
        if (!user || user.role !== "admin") { navigate("/"); return; }
        load();
    }, [user, loading, navigate, load]);

    const decide = async (id, status, block) => {
        const decision_note = (note[id] || "").trim();
        if (decision_note.length < 3) { toast.error("Bitte eine kurze Begründung angeben (DSA Art. 17)."); return; }
        try {
            await api.patch(`/admin/listing-reports/${id}`, { status, decision_note, block_listing: !!block });
            toast.success("Entscheidung gespeichert");
            load();
        } catch { toast.error("Speichern fehlgeschlagen"); }
    };

    return (
        <div className="min-h-screen bg-gray-50" data-testid="admin-reports-page">
            <div className="bg-[#111827] text-white">
                <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
                    <button onClick={() => navigate("/admin")} className="text-xs text-gray-400 hover:text-white" data-testid="admin-reports-back">← Admin-Dashboard</button>
                    <div className="text-xs font-bold uppercase tracking-wider text-[#FFCC00] mb-2 mt-4">DSA · Notice-and-Action</div>
                    <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight flex items-center gap-3" data-testid="admin-reports-title">
                        <ShieldAlert className="w-9 h-9 text-[#FFCC00]" /> Gemeldete Angebote
                    </h1>
                    <p className="text-sm text-gray-400 mt-2">Geprüft & begründet entscheiden (DSA Art. 16/17). Offene Meldungen: <b className="text-white">{data?.open_count ?? "…"}</b></p>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-4 md:px-8 py-6">
                <div className="flex gap-2 mb-5">
                    {["open", "in_review", "actioned", "rejected", "all"].map((f) => (
                        <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-sm text-sm font-bold ${filter === f ? "bg-[#111827] text-white" : "bg-white border border-gray-300 hover:bg-gray-50"}`} data-testid={`admin-reports-filter-${f}`}>
                            {f === "all" ? "Alle" : STATUS_LABEL[f]}
                        </button>
                    ))}
                </div>

                {busy && <div className="flex items-center gap-2 text-gray-500 text-sm py-8"><Loader2 className="w-4 h-4 animate-spin" /> Lade…</div>}

                {data && !busy && (
                    <div className="space-y-3">
                        {data.reports.length === 0 && <div className="text-center text-gray-400 py-12" data-testid="admin-reports-empty">Keine Meldungen in dieser Ansicht.</div>}
                        {data.reports.map((r) => (
                            <div key={r.id} className="rounded-lg border border-gray-200 bg-white p-4" data-testid={`admin-report-${r.id}`}>
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <Flag className="w-4 h-4 text-red-500" />
                                            <span className="font-bold text-gray-900">{r.reason_label}</span>
                                            <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${STATUS_STYLE[r.status] || "bg-gray-100"}`}>{STATUS_LABEL[r.status] || r.status}</span>
                                        </div>
                                        <div className="text-sm text-gray-700 mt-1.5">{r.detail}</div>
                                        <div className="text-xs text-gray-400 mt-1">
                                            Inserat: {r.listing_title || r.listing_id} · gemeldet {new Date(r.created_at).toLocaleString("de-DE")}
                                            {r.reporter_email ? ` · ${r.reporter_email}` : " · anonym"}
                                        </div>
                                    </div>
                                    <a href={`/listing/${r.listing_id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline" data-testid={`admin-report-open-${r.id}`}>
                                        Inserat ansehen <ExternalLink className="w-3 h-3" />
                                    </a>
                                </div>

                                {r.status === "open" || r.status === "in_review" ? (
                                    <div className="mt-3 border-t border-gray-100 pt-3">
                                        <textarea
                                            value={note[r.id] || ""}
                                            onChange={(e) => setNote({ ...note, [r.id]: e.target.value })}
                                            placeholder="Begründung der Entscheidung (Pflicht, DSA Art. 17)…"
                                            rows={2}
                                            className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm"
                                            data-testid={`admin-report-note-${r.id}`}
                                        />
                                        <div className="flex gap-2 mt-2 flex-wrap">
                                            <button onClick={() => decide(r.id, "actioned", true)} className="inline-flex items-center gap-1.5 rounded-sm bg-red-600 text-white px-3 py-1.5 text-sm font-bold hover:bg-red-700" data-testid={`admin-report-block-${r.id}`}>
                                                <X className="w-4 h-4" /> Inserat sperren
                                            </button>
                                            <button onClick={() => decide(r.id, "rejected", false)} className="inline-flex items-center gap-1.5 rounded-sm bg-white border border-gray-300 px-3 py-1.5 text-sm font-bold hover:bg-gray-50" data-testid={`admin-report-reject-${r.id}`}>
                                                <Check className="w-4 h-4" /> Meldung ablehnen
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    r.decision_note && <div className="mt-2 text-xs text-gray-500 border-t border-gray-100 pt-2"><b>Entscheidung:</b> {r.decision_note} {r.decided_by ? `(${r.decided_by})` : ""}</div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
